/**
 * Admin Volunteers Tab - Manage volunteer applications and scheduling
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Eye, CheckCircle, X, Plus, CalendarDays, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { useAdmin } from "./AdminContext";
import { API } from "../../lib/api";

const AdminVolunteersTab = () => {
  const { volunteers, volunteerSchedules, loadData } = useAdmin();
  
  const [volunteerFilter, setVolunteerFilter] = useState("all");
  const [viewingVolunteer, setViewingVolunteer] = useState(null);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ 
    volunteer_id: "", volunteer_name: "", date: "", shift: "", task: "", notes: "" 
  });

  const handleUpdateStatus = async (applicationId, status) => {
    try {
      await axios.put(`${API}/volunteers/${applicationId}/status`, { status }, { withCredentials: true });
      toast.success(`Volunteer ${status}!`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    }
  };

  const handleCreateSchedule = async () => {
    if (!scheduleForm.volunteer_id || !scheduleForm.date || !scheduleForm.shift || !scheduleForm.task) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      const vol = volunteers.find(v => v.application_id === scheduleForm.volunteer_id);
      await axios.post(`${API}/volunteers/schedules`, {
        volunteer_id: scheduleForm.volunteer_id,
        volunteer_name: vol?.name || "",
        date: scheduleForm.date,
        shift: scheduleForm.shift,
        task: scheduleForm.task,
        notes: scheduleForm.notes || ""
      }, { withCredentials: true });
      toast.success("Shift scheduled successfully");
      setShowScheduleForm(false);
      setScheduleForm({ volunteer_id: "", volunteer_name: "", date: "", shift: "", task: "", notes: "" });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to schedule shift");
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!confirm("Delete this scheduled shift?")) return;
    try {
      await axios.delete(`${API}/volunteers/schedules/${scheduleId}`, { withCredentials: true });
      toast.success("Shift deleted");
      loadData();
    } catch {
      toast.error("Failed to delete shift");
    }
  };

  return (
    <>
      {/* Volunteer Applications */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Volunteer Applications</CardTitle>
            <CardDescription>Review and manage volunteer applications</CardDescription>
          </div>
          <Select value={volunteerFilter} onValueChange={setVolunteerFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {volunteers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-600">No volunteer applications yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {volunteers
                .filter(v => volunteerFilter === "all" || v.status === volunteerFilter)
                .map(vol => (
                <div key={vol.application_id} className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{vol.name}</h3>
                      <Badge variant={vol.status === "approved" ? "default" : vol.status === "pending" ? "secondary" : "destructive"}>
                        {vol.status}
                      </Badge>
                      {vol.can_foster && <Badge variant="outline" className="bg-green-50 text-green-700">Can Foster</Badge>}
                      {vol.has_transport && <Badge variant="outline" className="bg-blue-50 text-blue-700">Has Transport</Badge>}
                    </div>
                    <p className="text-sm text-stone-600">{vol.email} • {vol.phone}</p>
                    <p className="text-xs text-stone-500 mt-1">
                      Interests: {vol.interests?.slice(0, 3).join(", ")}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setViewingVolunteer(vol)}>
                      <Eye className="w-3 h-3 mr-1" /> View
                    </Button>
                    {vol.status === "pending" && (
                      <>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleUpdateStatus(vol.application_id, "approved")}>
                          <CheckCircle className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleUpdateStatus(vol.application_id, "rejected")}>
                          <X className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Volunteer Scheduling */}
      <Card className="mt-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Volunteer Schedule</CardTitle>
            <CardDescription>Manage volunteer shifts and assignments</CardDescription>
          </div>
          <Button onClick={() => setShowScheduleForm(true)} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" /> Add Shift
          </Button>
        </CardHeader>
        <CardContent>
          {/* Add Shift Form */}
          {showScheduleForm && (
            <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
              <h4 className="font-semibold text-stone-900 mb-4">Schedule New Shift</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label>Volunteer *</Label>
                  <Select 
                    value={scheduleForm.volunteer_id} 
                    onValueChange={(val) => {
                      const vol = volunteers.find(v => v.application_id === val);
                      setScheduleForm({
                        ...scheduleForm, 
                        volunteer_id: val,
                        volunteer_name: vol?.name || ""
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select volunteer" />
                    </SelectTrigger>
                    <SelectContent>
                      {volunteers.filter(v => v.status === "approved").map(vol => (
                        <SelectItem key={vol.application_id} value={vol.application_id}>
                          {vol.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date *</Label>
                  <Input 
                    type="date" 
                    value={scheduleForm.date}
                    onChange={(e) => setScheduleForm({...scheduleForm, date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Shift *</Label>
                  <Select 
                    value={scheduleForm.shift} 
                    onValueChange={(val) => setScheduleForm({...scheduleForm, shift: val})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select shift" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="morning">Morning (8am-12pm)</SelectItem>
                      <SelectItem value="afternoon">Afternoon (12pm-5pm)</SelectItem>
                      <SelectItem value="evening">Evening (5pm-8pm)</SelectItem>
                      <SelectItem value="full_day">Full Day</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Task *</Label>
                  <Select 
                    value={scheduleForm.task} 
                    onValueChange={(val) => setScheduleForm({...scheduleForm, task: val})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select task" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cat_care">Cat Care & Feeding</SelectItem>
                      <SelectItem value="cleaning">Cleaning & Maintenance</SelectItem>
                      <SelectItem value="socializing">Cat Socializing</SelectItem>
                      <SelectItem value="transport">Animal Transport</SelectItem>
                      <SelectItem value="admin">Admin/Social Media</SelectItem>
                      <SelectItem value="fostering">Foster Check-in</SelectItem>
                      <SelectItem value="events">Event Support</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-4">
                <Label>Notes</Label>
                <Input 
                  placeholder="Optional notes for this shift..."
                  value={scheduleForm.notes || ""}
                  onChange={(e) => setScheduleForm({...scheduleForm, notes: e.target.value})}
                />
              </div>
              <div className="flex gap-2 mt-4">
                <Button className="bg-green-600 hover:bg-green-700" onClick={handleCreateSchedule}>
                  Save Shift
                </Button>
                <Button variant="outline" onClick={() => {
                  setShowScheduleForm(false);
                  setScheduleForm({ volunteer_id: "", volunteer_name: "", date: "", shift: "", task: "", notes: "" });
                }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Schedule Calendar View */}
          {volunteerSchedules.length === 0 ? (
            <div className="text-center py-8">
              <CalendarDays className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-600">No shifts scheduled yet</p>
              <p className="text-sm text-stone-500 mt-1">Click "Add Shift" to schedule volunteers</p>
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(
                volunteerSchedules.reduce((acc, sched) => {
                  const date = sched.date;
                  if (!acc[date]) acc[date] = [];
                  acc[date].push(sched);
                  return acc;
                }, {})
              )
              .sort(([a], [b]) => new Date(a) - new Date(b))
              .map(([date, schedules]) => (
                <div key={date} className="border rounded-lg overflow-hidden">
                  <div className="bg-stone-100 px-4 py-2 font-medium text-stone-800">
                    {new Date(date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                  <div className="divide-y">
                    {schedules.map(sched => (
                      <div key={sched.schedule_id} className="p-3 flex items-center justify-between hover:bg-stone-50">
                        <div className="flex items-center gap-4">
                          <Badge variant="outline" className={
                            sched.shift === "morning" ? "bg-yellow-50 text-yellow-700 border-yellow-200" :
                            sched.shift === "afternoon" ? "bg-orange-50 text-orange-700 border-orange-200" :
                            sched.shift === "evening" ? "bg-purple-50 text-purple-700 border-purple-200" :
                            "bg-blue-50 text-blue-700 border-blue-200"
                          }>
                            {sched.shift === "morning" ? "🌅 Morning" :
                             sched.shift === "afternoon" ? "☀️ Afternoon" :
                             sched.shift === "evening" ? "🌙 Evening" : "📅 Full Day"}
                          </Badge>
                          <div>
                            <p className="font-medium">{sched.volunteer_name}</p>
                            <p className="text-sm text-stone-600">
                              {sched.task === "cat_care" ? "🐱 Cat Care & Feeding" :
                               sched.task === "cleaning" ? "🧹 Cleaning & Maintenance" :
                               sched.task === "socializing" ? "💕 Cat Socializing" :
                               sched.task === "transport" ? "🚗 Animal Transport" :
                               sched.task === "admin" ? "💻 Admin/Social Media" :
                               sched.task === "fostering" ? "🏠 Foster Check-in" :
                               sched.task === "events" ? "🎉 Event Support" : "📝 Other"}
                            </p>
                            {sched.notes && <p className="text-xs text-stone-500 mt-1">{sched.notes}</p>}
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteSchedule(sched.schedule_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Volunteer Dialog */}
      <Dialog open={!!viewingVolunteer} onOpenChange={() => setViewingVolunteer(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {viewingVolunteer && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {viewingVolunteer.name}
                  <Badge variant={viewingVolunteer.status === "approved" ? "default" : viewingVolunteer.status === "pending" ? "secondary" : "destructive"}>
                    {viewingVolunteer.status}
                  </Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div><strong>Email:</strong> {viewingVolunteer.email}</div>
                  <div><strong>Phone:</strong> {viewingVolunteer.phone}</div>
                  <div><strong>Age Group:</strong> {viewingVolunteer.age_group}</div>
                  <div><strong>Has Transport:</strong> {viewingVolunteer.has_transport ? "Yes" : "No"}</div>
                </div>
                <div>
                  <strong>Address:</strong>
                  <p className="text-stone-600">{viewingVolunteer.address}</p>
                </div>
                <div>
                  <strong>Availability:</strong>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {viewingVolunteer.availability?.map(a => (
                      <Badge key={a} variant="outline">{a}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <strong>Interests:</strong>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {viewingVolunteer.interests?.map(i => (
                      <Badge key={i} variant="secondary">{i}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <strong>Can Foster:</strong> {viewingVolunteer.can_foster ? "Yes" : "No"}
                </div>
                <div>
                  <strong>Experience:</strong>
                  <p className="text-stone-600 bg-stone-50 p-3 rounded mt-1">{viewingVolunteer.experience}</p>
                </div>
                <div>
                  <strong>Why They Want to Volunteer:</strong>
                  <p className="text-stone-600 bg-stone-50 p-3 rounded mt-1">{viewingVolunteer.why_volunteer}</p>
                </div>
                {viewingVolunteer.additional_info && (
                  <div>
                    <strong>Additional Info:</strong>
                    <p className="text-stone-600">{viewingVolunteer.additional_info}</p>
                  </div>
                )}
                <p className="text-xs text-stone-500">
                  Applied: {new Date(viewingVolunteer.created_at).toLocaleString()}
                </p>
              </div>
              <DialogFooter>
                {viewingVolunteer.status === "pending" && (
                  <>
                    <Button className="bg-green-600 hover:bg-green-700" onClick={() => {
                      handleUpdateStatus(viewingVolunteer.application_id, "approved");
                      setViewingVolunteer(null);
                    }}>
                      <CheckCircle className="w-4 h-4 mr-2" /> Approve
                    </Button>
                    <Button variant="destructive" onClick={() => {
                      handleUpdateStatus(viewingVolunteer.application_id, "rejected");
                      setViewingVolunteer(null);
                    }}>
                      <X className="w-4 h-4 mr-2" /> Reject
                    </Button>
                  </>
                )}
                <Button variant="outline" onClick={() => setViewingVolunteer(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminVolunteersTab;
